"use client";

import { cn } from "@garther/ui/libs";
import { ChevronLeft, ChevronRight } from "lucide-react";
import React, { useEffect } from "react";

import { useIntersectionObserver } from "../../../hooks/use-intersection-observer";
import { Button } from "../Button";
import { Spinner } from "../Spinner";

// ---------------------------------------------------------------------------
// Internal: ListPagination
// ---------------------------------------------------------------------------

function ListPagination({
  currentPage,
  totalPages,
  onPageChange
}: {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}) {
  const getPageNumbers = (): (number | string)[] => {
    const pages: (number | string)[] = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);

      if (currentPage > 3) pages.push("...");

      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);

      for (let i = start; i <= end; i++) {
        if (i !== 1 && i !== totalPages) pages.push(i);
      }

      if (currentPage < totalPages - 2) pages.push("...");
      if (totalPages > 1) pages.push(totalPages);
    }

    return pages;
  };

  if (totalPages <= 1) return null;

  const pageNumbers = getPageNumbers();

  return (
    <div className="flex items-center justify-center gap-2 mt-6">
      <Button
        variant="outline"
        disabled={currentPage === 1}
        padding="p-auto"
        className="w-8 h-8 rounded-lg"
        onClick={() => onPageChange(currentPage - 1)}
      >
        <ChevronLeft className="h-4 w-4" />
      </Button>

      <div className="flex items-center gap-1">
        {pageNumbers.map((page, index) => (
          <div key={index}>
            {page === "..." ? (
              <span className="px-2 text-text-secondary">...</span>
            ) : (
              <Button
                variant={page === currentPage ? "default" : "ghost"}
                padding="p-auto"
                className={cn("w-8 h-8 text-responsive-base rounded-lg", {
                  "hover:bg-background-highlight": page !== currentPage,
                  "bg-background-dark-secondary font-medium": page === currentPage
                })}
                onClick={() => onPageChange(page as number)}
              >
                {page}
              </Button>
            )}
          </div>
        ))}
      </div>

      <Button
        variant="outline"
        disabled={currentPage === totalPages}
        padding="p-auto"
        className="w-8 h-8 rounded-lg"
        onClick={() => onPageChange(currentPage + 1)}
      >
        <ChevronRight className="h-4 w-4" />
      </Button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Internal: InfiniteScrollFooter
// ---------------------------------------------------------------------------

function InfiniteScrollFooter({
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
  noMoreContent
}: {
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  onLoadMore: () => void;
  noMoreContent?: React.ReactNode;
}) {
  const { ref, isIntersecting } = useIntersectionObserver({
    enabled: (hasNextPage ?? false) && !isFetchingNextPage
  });

  useEffect(() => {
    if (isIntersecting && hasNextPage && !isFetchingNextPage) {
      onLoadMore();
    }
  }, [isIntersecting, hasNextPage, isFetchingNextPage, onLoadMore]);

  return (
    <>
      {isFetchingNextPage && (
        <div className="flex justify-center py-6">
          <Spinner className="w-6 h-6" />
        </div>
      )}
      {!hasNextPage && noMoreContent}
      <div ref={ref} />
    </>
  );
}

// ---------------------------------------------------------------------------
// ListView props
// ---------------------------------------------------------------------------

interface ListViewBaseProps<T> {
  items: T[];
  renderItem: (item: T, index: number) => React.ReactNode;
  keyExtractor: (item: T, index: number) => string | number;
  containerClassName?: string;
  className?: string;
}

interface PaginationVariantProps {
  variant: "pagination";
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  /** @default true */
  showPagination?: boolean;
}

interface InfiniteVariantProps {
  variant: "infinite";
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  onLoadMore: () => void;
  noMoreContent?: React.ReactNode;
}

export type ListViewProps<T> = ListViewBaseProps<T> & (PaginationVariantProps | InfiniteVariantProps);

// ---------------------------------------------------------------------------
// ListView
// ---------------------------------------------------------------------------

export function ListView<T>({
  items,
  renderItem,
  keyExtractor,
  containerClassName,
  className,
  ...variantProps
}: ListViewProps<T>) {
  return (
    <div className={cn("w-full", className)}>
      <div className={containerClassName}>
        {items.map((item, index) => (
          <React.Fragment key={keyExtractor(item, index)}>{renderItem(item, index)}</React.Fragment>
        ))}
      </div>

      {variantProps.variant === "pagination" && (variantProps.showPagination ?? true) && (
        <ListPagination
          currentPage={variantProps.currentPage}
          totalPages={variantProps.totalPages}
          onPageChange={variantProps.onPageChange}
        />
      )}

      {variantProps.variant === "infinite" && (
        <InfiniteScrollFooter
          hasNextPage={variantProps.hasNextPage}
          isFetchingNextPage={variantProps.isFetchingNextPage}
          onLoadMore={variantProps.onLoadMore}
          noMoreContent={variantProps.noMoreContent}
        />
      )}
    </div>
  );
}
